/**
 * Blog Downgrade Service (Frontend)
 *
 * Strikt getrennt vom FCMS-Service. Spricht nur die Blog-Downgrade-Routen
 * (/subscriptions/blog-downgrade/*) und behandelt nur YouTube-Kanäle.
 */

import { apiClient } from '../api/apiClient';

export interface BlogDowngradeChannelItem {
  id: string;
  channelTitle: string;
  thumbnailPath: string | null;
  addedAt: string;
}

export interface BlogDowngradeResources {
  youtubeChannels: BlogDowngradeChannelItem[];
  /** Neues Limit im Zielplan (null = unbegrenzt) */
  youtubeChannelLimit: number | null;
}

export interface BlogDowngradeSelection {
  id: string;
  userId: string;
  subscriptionId: string;
  pendingPlanId: string;
  retainedYoutubeChannelIds: string[];
  status: 'pending' | 'applied' | 'cancelled';
  targetYoutubeChannelLimit: number | null;
  createdAt: string;
  updatedAt: string;
  appliedAt: string | null;
}

export const getBlogDowngradeResources = async (): Promise<BlogDowngradeResources> => {
  const response = await apiClient.get('/subscriptions/blog-downgrade/resources');
  return response.data.data;
};

export const getBlogDowngradeSelection = async (): Promise<BlogDowngradeSelection | null> => {
  const response = await apiClient.get('/subscriptions/blog-downgrade/selection');
  return response.data.data;
};

export const saveBlogDowngradeSelection = async (
  retainedYoutubeChannelIds: string[]
): Promise<BlogDowngradeSelection> => {
  const response = await apiClient.post('/subscriptions/blog-downgrade/selection', {
    retainedYoutubeChannelIds,
  });
  return response.data.data;
};

// =====================================================
// JOURNAL-IMPACT-VORSCHAU
// =====================================================

export interface JournalImpactSampleEntry {
  entryId: string;
  createdAt: string;
  contentPreview: string;
  affectedMarkers: Array<{ title: string; videoId?: string }>;
}

export interface JournalImpactPreview {
  affectedEntryCount: number;
  affectedReferenceCount: number;
  sampleEntries: JournalImpactSampleEntry[];
}

export interface ExpireImpactPreview extends JournalImpactPreview {
  blogChannelCount: number;
}

/**
 * Vorschau: Welche Notizen + Verlinkungen werden vom geplanten Bitcoin-Downgrade
 * gekappt? Optional kann eine hypothetische Auswahl mitgeschickt werden.
 */
export const getDowngradeJournalImpact = async (
  retainedYoutubeChannelIds?: string[]
): Promise<JournalImpactPreview> => {
  const params: Record<string, string> = {};
  if (retainedYoutubeChannelIds && retainedYoutubeChannelIds.length > 0) {
    params.retainedYoutubeChannelIds = retainedYoutubeChannelIds.join(',');
  }
  const response = await apiClient.get('/subscriptions/blog-downgrade/journal-impact', { params });
  return response.data.data;
};

/**
 * Vorschau: Welche Notizen + Verlinkungen werden gekappt, wenn dieser Channel
 * im Blog-Kontext entfernt wird?
 */
export const getChannelRemovalJournalImpact = async (
  channelId: string
): Promise<JournalImpactPreview> => {
  const response = await apiClient.get(`/youtube/channels/${channelId}/journal-impact`);
  return response.data.data;
};

/**
 * Vorschau: Was passiert mit Blog-Channels und Journal-Verlinkungen, wenn die
 * Bitcoin-Sub komplett gekündigt wird (zum Periodenende).
 */
export const getExpireJournalImpact = async (): Promise<ExpireImpactPreview> => {
  const response = await apiClient.get('/subscriptions/expire-impact');
  return response.data.data;
};
